import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, CheckCircle, FileText, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import heroImage from "@/assets/blog-citizenship-naturalization.jpg";
import { generateArticleSchema } from "@/lib/seo-schemas";

const CitizenshipGuide = () => {
  const schema = generateArticleSchema(
    "Citizenship Through Naturalization: Complete Guide",
    "Everything you need to know about becoming a U.S. citizen through naturalization, from eligibility to the ceremony.",
    "2025-01-05",
    "2025-01-05",
    "https://danielalbertlaw.com/blog/citizenship-naturalization-guide"
  );

  return (
    <div className="min-h-screen">
      <SEO
        title="Citizenship Through Naturalization: Complete Guide | Daniel Albert Law"
        description="Everything you need to know about becoming a U.S. citizen through naturalization, from eligibility requirements to the citizenship ceremony."
        keywords="naturalization, US citizenship, citizenship test, green card to citizenship, Houston immigration attorney"
        schema={schema}
      />
      
      <Header />
      
      <Breadcrumbs
        items={[
          { label: "Blog", href: "/blog" },
          { label: "Citizenship Through Naturalization: Complete Guide" }
        ]}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl text-primary-foreground">
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="inline-block bg-accent/20 text-accent px-3 py-1 rounded-full font-semibold">
                Immigration
              </span>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>January 5, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Daniel Albert</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">
              Citizenship Through Naturalization: Complete Guide
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Becoming a U.S. citizen through naturalization is one of the most significant milestones in an immigrant's journey. Citizenship provides numerous benefits including the right to vote, eligibility for federal jobs, protection from deportation, and the ability to petition for family members. This comprehensive guide will walk you through every step of the naturalization process.
              </p>
            </div>

            {/* Benefits Card */}
            <Card className="mb-12 bg-gradient-subtle">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Award className="h-6 w-6 text-accent" />
                  Benefits of U.S. Citizenship
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Right to vote in federal elections</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Eligibility for federal employment and benefits</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Protection from deportation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Ability to sponsor family members for immigration</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>U.S. passport for international travel</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Citizenship for children born abroad</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Main Content Sections */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-bold mb-6">Eligibility Requirements</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  To qualify for naturalization, you must meet the following basic requirements:
                </p>
                
                <div className="space-y-6">
                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3">Age and Residency</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Be at least 18 years old</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Have been a permanent resident (green card holder) for at least 5 years (or 3 years if married to a U.S. citizen)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Have lived in the state or USCIS district where you're applying for at least 3 months</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3">Physical Presence and Continuous Residence</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Have been physically present in the U.S. for at least 30 months out of the 5 years (or 18 months out of 3 years if married to U.S. citizen)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Have maintained continuous residence without any trip abroad lasting 6 months or more</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3">Good Moral Character</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Demonstrate good moral character for at least 5 years (or 3 years if married to U.S. citizen)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>No serious criminal history</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Up to date on all taxes</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3">English and Civics</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Ability to read, write, and speak basic English (some exceptions apply)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Knowledge of U.S. history and government (civics test)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">The Naturalization Process: Step by Step</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Step 1: Determine Eligibility</h3>
                    <p className="text-lg text-muted-foreground">
                      Review the eligibility requirements carefully. If you're unsure about your eligibility, consult with an immigration attorney who can assess your specific situation.
                    </p>
                  </div>

                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Step 2: Prepare and Submit Form N-400</h3>
                    <p className="text-lg text-muted-foreground">
                      Complete Form N-400 (Application for Naturalization) with accurate information about your background, travel history, and residence. Submit it along with required documents and the filing fee (currently $725, including $640 application fee and $85 biometrics fee).
                    </p>
                  </div>

                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Step 3: Biometrics Appointment</h3>
                    <p className="text-lg text-muted-foreground">
                      After filing, you'll receive a notice to appear at a USCIS Application Support Center for fingerprinting and photographs. This typically occurs within 4-6 weeks of filing.
                    </p>
                  </div>

                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Step 4: Citizenship Interview and Test</h3>
                    <p className="text-lg text-muted-foreground mb-4">
                      You'll be scheduled for an interview with a USCIS officer who will review your application and test your English and civics knowledge. The civics test consists of questions about U.S. history and government. You must answer at least 6 out of 10 questions correctly.
                    </p>
                    <p className="text-lg text-muted-foreground">
                      The English test assesses your ability to read, write, and speak in English through conversation during the interview and by reading and writing simple sentences.
                    </p>
                  </div>

                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Step 5: Decision</h3>
                    <p className="text-lg text-muted-foreground">
                      USCIS will grant, continue, or deny your application. If granted, you'll receive a notice with the date, time, and location of your naturalization ceremony. If continued, you may need to provide additional evidence or retake the English/civics test. If denied, you have the right to appeal or reapply.
                    </p>
                  </div>

                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Step 6: Naturalization Ceremony</h3>
                    <p className="text-lg text-muted-foreground">
                      At the ceremony, you'll take the Oath of Allegiance to the United States, officially becoming a U.S. citizen. You'll receive your Certificate of Naturalization, which is proof of your citizenship. You must return your green card at the ceremony.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Preparing for the Citizenship Test</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  The civics test covers 100 questions about U.S. history and government. USCIS provides study materials including:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>The official 100 civics questions and answers (available in multiple languages)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Practice tests and study guides</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Mobile apps for studying on-the-go</span>
                  </li>
                </ul>
                <p className="text-lg text-muted-foreground mt-6">
                  Many community organizations and libraries also offer free citizenship classes and test preparation courses.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Common Reasons for Denial</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Applications can be denied for various reasons, including:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Not meeting the continuous residence or physical presence requirements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Failing to demonstrate good moral character</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Not passing the English or civics test (after two attempts)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Providing false information on the application</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Outstanding tax issues</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Criminal history that affects moral character</span>
                  </li>
                </ul>
              </section>
            </div>

            {/* Related Services */}
            <Card className="mt-12 bg-primary text-primary-foreground">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">Ready to Become a U.S. Citizen?</h2>
                <p className="text-lg mb-6 opacity-95">
                  Daniel Albert Law Firm has helped thousands of permanent residents achieve their dream of U.S. citizenship. We provide comprehensive support throughout the naturalization process, including application preparation, interview coaching, and test preparation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="outline" className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    <Link to="/practice-areas/immigration-services/citizenship-naturalization">
                      Learn More About Our Services
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <CTASection
        title="Start Your Journey to U.S. Citizenship"
        description="Contact us today. We'll review your eligibility and guide you through every step of the naturalization process."
      />

      <Footer />
    </div>
  );
};

export default CitizenshipGuide;